import * as Tone from 'tone'

export class Audio {
  private initialized = false
  private masterVolume = 0.5
  private musicVolume = 0.3
  private sfxVolume = 0.6

  // Synthesizers for different sound types
  private sfxSynth: Tone.PolySynth | null = null
  private bassSynth: Tone.MonoSynth | null = null
  private ambientSynth: Tone.PolySynth | null = null

  // Cooldowns to prevent audio spam
  private lastAttackTime: number = 0
  private lastHitTime: number = 0
  private lastDeathTime: number = 0
  private minSoundInterval: number = 0.08  // Minimum 80ms between same sounds

  // Effects
  private reverb: Tone.Reverb | null = null
  private delay: Tone.FeedbackDelay | null = null
  private filter: Tone.Filter | null = null

  // Ambient music state
  private ambientLoop: Tone.Loop | null = null
  private isPlayingAmbient = false
  private gameIntensity = 0  // 0 = peaceful, 1 = combat

  async init(): Promise<void> {
    if (this.initialized) return

    // Create effects chain
    this.reverb = new Tone.Reverb({
      decay: 2,
      wet: 0.3
    }).toDestination()

    this.delay = new Tone.FeedbackDelay({
      delayTime: '8n',
      feedback: 0.2,
      wet: 0.1
    }).connect(this.reverb)

    this.filter = new Tone.Filter({
      frequency: 2000,
      type: 'lowpass'
    }).connect(this.delay)

    // Create synthesizers
    this.sfxSynth = new Tone.PolySynth(Tone.Synth, {
      oscillator: { type: 'triangle' },
      envelope: {
        attack: 0.01,
        decay: 0.1,
        sustain: 0.3,
        release: 0.2
      }
    }).connect(this.filter)
    this.sfxSynth.volume.value = -6

    this.bassSynth = new Tone.MonoSynth({
      oscillator: { type: 'sawtooth' },
      envelope: {
        attack: 0.1,
        decay: 0.3,
        sustain: 0.5,
        release: 0.5
      },
      filterEnvelope: {
        attack: 0.1,
        decay: 0.2,
        sustain: 0.5,
        release: 0.5,
        baseFrequency: 100,
        octaves: 2
      }
    }).connect(this.reverb)
    this.bassSynth.volume.value = -10

    this.ambientSynth = new Tone.PolySynth(Tone.Synth, {
      oscillator: { type: 'sine' },
      envelope: {
        attack: 1,
        decay: 0.5,
        sustain: 0.8,
        release: 2
      }
    }).connect(this.reverb)
    this.ambientSynth.volume.value = -15

    this.initialized = true
  }

  async startAudio(): Promise<void> {
    if (Tone.getContext().state !== 'running') {
      await Tone.start()
    }
  }

  // Sound effects
  playSelect(): void {
    if (!this.sfxSynth) return
    this.sfxSynth.triggerAttackRelease('C5', '32n', undefined, 0.3)
  }

  playClick(): void {
    if (!this.sfxSynth) return
    this.sfxSynth.triggerAttackRelease('E5', '64n', undefined, 0.4)
  }

  playBuild(): void {
    if (!this.sfxSynth) return
    const now = Tone.now()
    this.sfxSynth.triggerAttackRelease('C4', '8n', now, 0.5)
    this.sfxSynth.triggerAttackRelease('E4', '8n', now + 0.05, 0.4)
    this.sfxSynth.triggerAttackRelease('G4', '8n', now + 0.1, 0.3)
  }

  playComplete(): void {
    if (!this.sfxSynth) return
    const now = Tone.now()
    this.sfxSynth.triggerAttackRelease('G4', '16n', now, 0.5)
    this.sfxSynth.triggerAttackRelease('C5', '16n', now + 0.1, 0.5)
    this.sfxSynth.triggerAttackRelease('E5', '8n', now + 0.2, 0.4)
  }

  playAttack(): void {
    if (!this.bassSynth) return
    const now = Tone.now()
    // Throttle attack sounds to prevent audio overload
    if (now - this.lastAttackTime < this.minSoundInterval) return
    this.lastAttackTime = now
    try {
      this.bassSynth.triggerAttackRelease('C2', '16n', now + 0.02, 0.7)
    } catch {
      // Ignore timing errors - audio state can get out of sync
    }
  }

  playHit(): void {
    if (!this.sfxSynth) return
    const now = Tone.now()
    // Throttle hit sounds
    if (now - this.lastHitTime < this.minSoundInterval) return
    this.lastHitTime = now
    try {
      this.sfxSynth.triggerAttackRelease(['C3', 'E3'], '32n', now + 0.02, 0.6)
    } catch {
      // Ignore timing errors
    }
  }

  playDeath(): void {
    if (!this.bassSynth) return
    const now = Tone.now()
    // Throttle death sounds
    if (now - this.lastDeathTime < this.minSoundInterval * 2) return
    this.lastDeathTime = now
    try {
      this.bassSynth.triggerAttackRelease('E2', '8n', now + 0.02, 0.6)
      this.bassSynth.triggerAttackRelease('C2', '4n', now + 0.17, 0.4)
    } catch {
      // Ignore timing errors
    }
  }

  playGather(): void {
    if (!this.sfxSynth) return
    this.sfxSynth.triggerAttackRelease('A4', '32n', undefined, 0.2)
  }

  playError(): void {
    if (!this.sfxSynth) return
    const now = Tone.now()
    this.sfxSynth.triggerAttackRelease('E3', '16n', now, 0.5)
    this.sfxSynth.triggerAttackRelease('C3', '8n', now + 0.1, 0.5)
  }

  playVictory(): void {
    if (!this.sfxSynth) return
    const now = Tone.now()
    const notes = ['C4', 'E4', 'G4', 'C5', 'E5', 'G5', 'C6']
    notes.forEach((note, i) => {
      this.sfxSynth!.triggerAttackRelease(note, '8n', now + i * 0.1, 0.5 - i * 0.05)
    })
  }

  playDefeat(): void {
    if (!this.bassSynth || !this.sfxSynth) return
    const now = Tone.now()
    this.bassSynth.triggerAttackRelease('C2', '2n', now, 0.6)
    this.sfxSynth.triggerAttackRelease('C3', '4n', now + 0.2, 0.4)
    this.sfxSynth.triggerAttackRelease('B2', '4n', now + 0.4, 0.3)
  }

  // Ambient music
  startAmbientMusic(): void {
    if (this.isPlayingAmbient || !this.ambientSynth) return

    const chords = [
      ['C3', 'E3', 'G3'],
      ['A2', 'C3', 'E3'],
      ['F2', 'A2', 'C3'],
      ['G2', 'B2', 'D3']
    ]

    let chordIndex = 0

    this.ambientLoop = new Tone.Loop((time) => {
      if (!this.ambientSynth) return

      const chord = chords[chordIndex % chords.length]
      const intensity = this.gameIntensity

      // Adjust music based on game intensity
      this.ambientSynth.triggerAttackRelease(
        chord,
        '2n',
        time,
        0.2 + intensity * 0.2
      )

      // Add bass note during combat
      if (intensity > 0.5 && this.bassSynth) {
        this.bassSynth.triggerAttackRelease(
          chord[0].replace('3', '2'),
          '4n',
          time + 0.5,
          0.3
        )
      }

      chordIndex++
    }, '2n')

    this.ambientLoop.start(0)
    Tone.getTransport().start()
    this.isPlayingAmbient = true
  }

  stopAmbientMusic(): void {
    if (this.ambientLoop) {
      this.ambientLoop.stop()
      this.ambientLoop.dispose()
      this.ambientLoop = null
    }
    Tone.getTransport().stop()
    this.isPlayingAmbient = false
  }

  // Set game intensity (affects ambient music)
  setGameIntensity(intensity: number): void {
    this.gameIntensity = Math.max(0, Math.min(1, intensity))
  }

  // Volume controls
  setMasterVolume(volume: number): void {
    this.masterVolume = Math.max(0, Math.min(1, volume))
    Tone.getDestination().volume.value = Tone.gainToDb(this.masterVolume)
  }

  setMusicVolume(volume: number): void {
    this.musicVolume = Math.max(0, Math.min(1, volume))
    if (this.ambientSynth) {
      this.ambientSynth.volume.value = Tone.gainToDb(this.musicVolume) - 15
    }
  }

  setSFXVolume(volume: number): void {
    this.sfxVolume = Math.max(0, Math.min(1, volume))
    if (this.sfxSynth) {
      this.sfxSynth.volume.value = Tone.gainToDb(this.sfxVolume) - 6
    }
    if (this.bassSynth) {
      this.bassSynth.volume.value = Tone.gainToDb(this.sfxVolume) - 10
    }
  }

  destroy(): void {
    this.stopAmbientMusic()
    this.sfxSynth?.dispose()
    this.bassSynth?.dispose()
    this.ambientSynth?.dispose()
    this.reverb?.dispose()
    this.delay?.dispose()
    this.filter?.dispose()
  }
}
